import React from 'react';
import { localTimeOf } from './localTime';

import type { LaneBooking } from '../../../apis/lanePlanner';
import { isConfirming, isRefused } from './useLaneBooking';
import { formatMoney } from './quoteView';
import styles from './styles.module.css';

const NEXT_STEPS = [
  'The carrier confirms the booking, usually within a few hours.',
  'You receive the air waybill and pickup details from the carrier.',
  'Track the shipment from Shipments once it is created there.',
];

const Fact: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) =>
  value ? (
    <div className={styles.detailRow}>
      <span>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  ) : null;

interface BookingConfirmationProps {
  booking: LaneBooking;
  stalled: boolean;
  /** IANA zone of the departure airport, for a departure the vendor wrote in UTC. */
  departureZone?: string | null;
  /** The quote's computed travel text; the booking's snapshot has no legs to compute it from. */
  transit?: string | null;
  onViewShipments: () => void;
  onPlanAnother: () => void;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  booking,
  stalled,
  departureZone,
  transit,
  onViewShipments,
  onPlanAnother,
}) => {
  const { quote } = booking;
  const confirming = isConfirming(booking);
  const refused = isRefused(booking);
  return (
    <div className={styles.confirmation}>
      <div
        className={`${styles.confirmationMark} ${
          refused ? styles.confirmationMarkRefused : ''
        }`}
        aria-hidden
      >
        {refused ? '!' : '✓'}
      </div>
      <h3 className={styles.confirmationTitle}>
        {refused
          ? 'Booking not accepted'
          : confirming
            ? 'Booking placed'
            : 'Booking confirmed'}
      </h3>
      {refused && (
        <div className={styles.muted}>
          The carrier did not accept this booking. Contact support with the
          reference below.
        </div>
      )}
      {confirming && (
        <div className={styles.muted}>
          {stalled
            ? 'The carrier has not confirmed yet. Check Shipments later, or contact support with the reference below.'
            : 'Waiting for the carrier to confirm. This page updates by itself.'}
        </div>
      )}
      <div className={`${styles.muted} ${styles.refLabel}`}>
        Booking reference
      </div>
      <div className={styles.confirmationRef}>{booking.reference}</div>
      <div className={`${styles.bookingCard} ${styles.fullWidth}`}>
        <Fact
          label='Lane'
          value={
            quote.origin && quote.destination
              ? `${quote.origin} → ${quote.destination}`
              : null
          }
        />
        <Fact label='Carrier' value={quote.carrier} />
        <Fact
          label='Departure'
          value={localTimeOf(quote.departs_at, departureZone)?.date ?? null}
        />
        <Fact label='Transit' value={transit ?? null} />
        <Fact label='Air waybill' value={booking.awb_number} />
        <Fact label='Total' value={formatMoney(quote.price, quote.currency)} />
      </div>
      <h4 className={`${styles.bookingCardTitle} ${styles.alignStart}`}>
        What happens next
      </h4>
      <ol className={styles.nextSteps}>
        {NEXT_STEPS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <div className={`${styles.bookingActions} ${styles.fullWidth}`}>
        <button
          type='button'
          className={styles.secondaryAction}
          onClick={onViewShipments}
        >
          View in Shipments
        </button>
        <button
          type='button'
          className={styles.primaryAction}
          onClick={onPlanAnother}
        >
          Plan another lane
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmation;
